<template>
  <div class="rounded-lg overflow-hidden bg-black ring-1 ring-gray-300 dark:ring-gray-700 shadow-sm">
    <div class="flex items-center gap-1.5 bg-gray-100 px-3 py-2 dark:bg-gray-800">
      <span class="h-2.5 w-2.5 rounded-full bg-red-400"></span>
      <span class="h-2.5 w-2.5 rounded-full bg-yellow-400"></span>
      <span class="h-2.5 w-2.5 rounded-full bg-green-400 cursor-pointer hover:bg-green-500" @click="toggleFullscreen"></span>
      <span v-if="title" class="mx-auto text-xs text-gray-500 dark:text-gray-400 truncate">Weektaak.com instructie — {{ title }}</span>
    </div>
    <div ref="videoWrap" class="relative">
      <video
        ref="videoEl"
        :key="videoKey"
        controls
        playsinline
        class="w-full"
        :class="{ 'opacity-0': switching }"
        @ended="onVideoEnded"
      >
        <source :src="videoUrl('webm')" type="video/webm">
        <source :src="videoUrl('mp4')" type="video/mp4">
        <track
          v-for="subLang in subtitleLanguages"
          :key="subLang"
          kind="subtitles"
          :src="subtitleUrl(subLang)"
          :srclang="subLang"
          :label="langNames[subLang] || subLang"
        >
      </video>

      <!-- Auto-play next overlay -->
      <div v-if="countdown !== null" class="absolute inset-0 flex items-center justify-center bg-black/80">
        <div class="text-center">
          <div class="relative mx-auto mb-4 h-20 w-20">
            <svg class="h-20 w-20 -rotate-90" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="36" fill="none" stroke="#374151" stroke-width="4" />
              <circle cx="40" cy="40" r="36" fill="none" stroke="#3b82f6" stroke-width="4"
                stroke-linecap="round"
                :stroke-dasharray="226.2"
                :stroke-dashoffset="226.2 * (1 - countdown / 10)"
                class="transition-[stroke-dashoffset] duration-1000 ease-linear"
              />
            </svg>
            <span class="absolute inset-0 flex items-center justify-center text-2xl font-bold text-white">{{ countdown }}</span>
          </div>
          <p class="mb-4 text-sm text-gray-300">{{ nextVideoTitle }}</p>
          <div class="flex justify-center gap-3">
            <button @click="cancelCountdown" class="rounded bg-gray-700 px-3 py-1.5 text-xs font-medium text-gray-300 hover:bg-gray-600 hover:text-white transition-colors">
              Annuleren
            </button>
            <button @click="skipCountdown" class="rounded bg-brand px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700 transition-colors">
              Nu afspelen
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center gap-3 bg-gray-100 px-3 py-2 dark:bg-gray-900">
      <!-- Language selector -->
      <select
        v-if="activeVideo.languages && activeVideo.languages.length > 1"
        v-model="lang"
        class="rounded border-0 bg-white py-1 pl-2 pr-7 text-xs text-gray-700 ring-1 ring-gray-300 focus:ring-2 focus:ring-brand sm:text-sm dark:bg-gray-800 dark:text-white dark:ring-gray-700"
      >
        <option v-for="l in activeVideo.languages" :key="l" :value="l">{{ langNames[l] || l }}</option>
      </select>

      <!-- Theme toggle (grouped) -->
      <div v-if="hasMultipleThemes" class="inline-flex rounded ring-1 ring-gray-300 dark:ring-gray-700">
        <button
          @click="setTheme('light')"
          :class="[
            'inline-flex items-center gap-1 rounded-l px-2 py-1 text-xs font-medium transition-colors',
            theme === 'light'
              ? 'bg-brand text-white dark:bg-white dark:text-gray-900'
              : 'bg-white text-gray-500 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-white'
          ]"
        >
          <i class="fas fa-sun"></i>
          Licht
        </button>
        <button
          @click="setTheme('dark')"
          :class="[
            'inline-flex items-center gap-1 rounded-r px-2 py-1 text-xs font-medium transition-colors',
            theme === 'dark'
              ? 'bg-brand text-white dark:bg-white dark:text-gray-900'
              : 'bg-white text-gray-500 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:text-white'
          ]"
        >
          <i class="fas fa-moon"></i>
          Donker
        </button>
      </div>

      <!-- Title -->
      <span v-if="title" class="ml-auto truncate text-xs text-gray-500 dark:text-gray-400">{{ title }}</span>
    </div>

    <!-- Table of contents (series) -->
    <div v-if="seriesVideos.length > 0 && !hideSeries" class="border-t border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900">
      <p v-if="seriesTitle" class="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{{ seriesTitle }}</p>
      <ol class="space-y-0.5">
        <li v-for="(item, index) in seriesVideos" :key="item.slug">
          <button
            @click="goToSeries(item)"
            :class="[
              'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors',
              item.slug === activeSlug ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:bg-gray-200/50 dark:text-gray-400 dark:hover:bg-white/5'
            ]"
          >
            <span :class="[
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold',
              item.slug === activeSlug ? 'bg-brand text-white' : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            ]">{{ index + 1 }}</span>
            <span class="flex-1 truncate">{{ itemTitle(item) }}</span>
            <i v-if="item.slug === activeSlug" class="fas fa-play text-[8px] text-brand"></i>
          </button>
        </li>
      </ol>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TutorialVideo',
  props: {
    video: {
      type: Object,
      required: true
    },
    defaultLang: {
      type: String,
      default: 'nl'
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    hideSeries: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeSlug: this.video.slug,
      lang: this.defaultLang,
      theme: this.detectTheme(),
      themeManual: false,
      switching: false,
      countdown: null,
      countdownTimer: null,
      langNames: {
        nl: 'Nederlands',
        en: 'English',
        de: 'Deutsch'
      }
    }
  },
  computed: {
    seriesVideos() {
      return this.video.series?.videos || []
    },
    activeVideo() {
      const found = this.seriesVideos.find(v => v.slug === this.activeSlug)
      return found || this.video
    },
    seriesTitle() {
      const title = this.video.series?.title
      if (!title) return ''
      return title[this.lang] || title['nl'] || ''
    },
    videoKey() {
      return `${this.activeVideo.slug}-${this.theme}-${this.lang}`
    },
    title() {
      if (!this.activeVideo.titles) return null
      return this.activeVideo.titles[this.lang] || this.activeVideo.titles['nl'] || null
    },
    subtitleLanguages() {
      return this.activeVideo.subtitle_languages || []
    },
    hasMultipleThemes() {
      return this.activeVideo.themes && this.activeVideo.themes.length > 1
    },
    hasNext() {
      if (this.seriesVideos.length === 0) return false
      const idx = this.seriesVideos.findIndex(v => v.slug === this.activeSlug)
      return idx >= 0 && idx < this.seriesVideos.length - 1
    },
    nextVideoTitle() {
      if (!this.hasNext) return null
      const idx = this.seriesVideos.findIndex(v => v.slug === this.activeSlug)
      const next = this.seriesVideos[idx + 1]
      if (!next?.titles) return null
      return next.titles[this.lang] || next.titles['nl'] || null
    }
  },
  watch: {
    video: {
      handler(newVideo) {
        this.activeSlug = newVideo.slug
      },
      deep: true
    }
  },
  methods: {
    videoUrl(ext) {
      return `/videos/${this.activeVideo.slug}-${this.theme}-${this.lang}.${ext}`
    },
    subtitleUrl(subLang) {
      const base = `/videos/${this.activeVideo.slug}-${this.theme}-${this.lang}`
      return subLang === this.lang ? `${base}.vtt` : `${base}.${subLang}.vtt`
    },
    detectTheme() {
      if (typeof document === 'undefined') return 'light'
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light'
    },
    onThemeChange() {
      if (!this.themeManual) {
        this.theme = this.detectTheme()
      }
    },
    setTheme(t) {
      if (t === this.theme) return
      const video = this.$refs.videoEl
      const wrap = this.$refs.videoWrap
      const progress = video && video.duration ? video.currentTime / video.duration : 0
      const wasPlaying = video && !video.paused

      // Capture current frame as a canvas overlay
      let snapshot = null
      if (video && video.videoWidth && wrap) {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        Object.assign(canvas.style, {
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          zIndex: '10',
          transition: 'opacity 0.3s ease',
          opacity: '1',
          pointerEvents: 'none'
        })
        wrap.appendChild(canvas)
        snapshot = canvas

        // Lock container height to prevent layout shift
        wrap.style.height = wrap.offsetHeight + 'px'
      }

      // Pause old video before switching to prevent audio overlap
      if (video) video.pause()

      this.switching = true
      this.theme = t
      this.themeManual = true

      this.$nextTick(() => {
        const newVideo = this.$refs.videoEl
        if (!newVideo) {
          this._cleanupSnapshot(snapshot, wrap)
          return
        }
        const reveal = () => {
          if (progress) {
            newVideo.currentTime = newVideo.duration * progress
          }
          if (wasPlaying) newVideo.play()
          this.switching = false
          if (snapshot) {
            snapshot.style.opacity = '0'
            snapshot.addEventListener('transitionend', () => {
              snapshot.remove()
              if (wrap) wrap.style.height = ''
            }, { once: true })
            // Fallback: remove after 400ms in case transitionend doesn't fire
            setTimeout(() => {
              if (snapshot.parentNode) snapshot.remove()
              if (wrap) wrap.style.height = ''
            }, 400)
          } else if (wrap) {
            wrap.style.height = ''
          }
        }
        if (snapshot) {
          // Wait until new video is ready before revealing
          newVideo.addEventListener('canplay', reveal, { once: true })
          // Fallback: if canplay never fires (e.g. network issue), reveal after 3s
          setTimeout(() => {
            if (this.switching) {
              this.switching = false
              this._cleanupSnapshot(snapshot, wrap)
            }
          }, 3000)
        } else {
          // No snapshot (video hadn't loaded yet), just restore position
          if (progress) {
            const restore = () => {
              newVideo.currentTime = newVideo.duration * progress
              if (wasPlaying) newVideo.play()
            }
            newVideo.addEventListener('loadedmetadata', restore, { once: true })
          }
          this.switching = false
        }
      })
    },
    _cleanupSnapshot(snapshot, wrap) {
      if (snapshot && snapshot.parentNode) snapshot.remove()
      if (wrap) wrap.style.height = ''
    },
    play() {
      this.$refs.videoEl?.play()
    },
    toggleFullscreen() {
      const video = this.$refs.videoEl
      if (!video) return
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        video.requestFullscreen()
      }
    },
    goToSeries(item) {
      this.cancelCountdown()
      this.activeSlug = item.slug
      if (item.languages && !item.languages.includes(this.lang)) {
        this.lang = item.languages[0] || this.defaultLang
      }
    },
    itemTitle(item) {
      if (!item.titles) return ''
      return item.titles[this.lang] || item.titles['nl'] || ''
    },
    onVideoEnded() {
      if (!this.hasNext) return
      this.countdown = 10
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          this.skipCountdown()
        }
      }, 1000)
    },
    cancelCountdown() {
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
      this.countdown = null
    },
    skipCountdown() {
      this.cancelCountdown()
      const idx = this.seriesVideos.findIndex(v => v.slug === this.activeSlug)
      if (idx >= 0 && idx < this.seriesVideos.length - 1) {
        this.goToSeries(this.seriesVideos[idx + 1])
        this.$nextTick(() => this.play())
      }
    }
  },
  mounted() {
    // Watch for .dark class changes on <html> (Tailwind 4 class-based dark mode)
    this._observer = new MutationObserver(() => this.onThemeChange())
    this._observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    if (this.autoplay) {
      this.$nextTick(() => this.play())
    }
  },
  beforeUnmount() {
    // Stop video playback to prevent audio continuing after unmount
    const video = this.$refs.videoEl
    if (video) {
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
    this.cancelCountdown()
    if (this._observer) {
      this._observer.disconnect()
    }
  }
}
</script>

<style scoped>
video::cue {
  font-size: 0.85em;
}
</style>
